import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Space, Dropdown, message, Modal, Typography, Empty, Spin, Tooltip } from 'antd';
import {
  BulbOutlined,
  DownOutlined,
  EyeOutlined,
  InboxOutlined,
  EditOutlined,
  SendOutlined,
  ClockCircleOutlined,
  UserSwitchOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import {
  getApplications,
  updateApplicationStatus,
  retryContact,
  getApplicationLocation,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { captureEvent } from '../../services/analytics';
import { maybeShowRejectionEncouragement } from '../../utils/rejectionEncouragement';
import { Application, ApplicationStatus } from '../../types';
import ApplicationDrawer from '../../components/drawers/ApplicationDrawer';
import ArchiveModal from '../../components/modals/ArchiveModal';
import ComposeEmailModal from '../../components/modals/ComposeEmailModal';
import InterviewKitModal from '../../components/modals/InterviewKitModal';
import { canGenerateInterviewKit } from '../../utils/interviewKit';
import ReplyCaptureBanner from '../../components/email/ReplyCaptureBanner';
import AutoApplyOfferCard from '../../components/autoApply/AutoApplyOfferCard';
import { useAutoApplyOffer } from '../../hooks/useAutoApplyOffer';
import { isDeclaredApplication, channelTagLabel } from '../../utils/applicationChannel';
import { useNotifications } from '../../contexts/NotificationContext';
import { contactLabel } from '../../utils/contactUtils';
import { useListPageState } from '../../hooks/useListPageState';
import {
  isApplicationsBannerActive,
  dismissApplicationsBanner,
  getGmailAddonUrl,
} from '../../utils/replyCaptureStorage';
import css from './Applications.module.scss';

const { Title, Text } = Typography;

// Applications tracked before the Auto-Apply offer is worth making. One is
// enough: by then the user has seen what an application looks like, which is
// the whole point of waiting at all. Counts every tracked application,
// including ones declared manually rather than sent through a Job Beast draft
// — sent-only is the stricter trust signal but needs a second request, and the
// gap is marginal for a discovery nudge.
//
// Still a named constant rather than an inlined `> 0`: the page early-returns
// its empty state at zero applications, so this guards intent, not arithmetic.
const AUTO_APPLY_OFFER_MIN_APPLICATIONS = 1;

// Applications after which Auto-Apply takes the shared offer slot from Reply
// Capture. Reply Capture goes quiet only when dismissed — it has no idea
// whether tracking actually works — so without this it holds the slot forever
// and Auto-Apply is never offered here at all. Yielding costs Reply Capture
// nothing permanent: it returns on its own BANNER_DECAY_DAYS timer.
const AUTO_APPLY_WINS_SLOT_AT = 5;

// Status badge color mapping
const statusColors: Record<ApplicationStatus, string> = {
  submitted: 'blue',
  under_review: 'gold',
  screening_call: 'green',
  first_interview: 'green',
  second_interview: 'green',
  additional_interview: 'green',
  technical_assessment: 'green',
  reference_check: 'green',
  offer_extended: 'purple',
  negotiation: 'purple',
  hired: 'gold',
  rejected: 'red',
  withdrawn: 'default',
};

// Status display name mapping
const statusDisplayNames: Record<ApplicationStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  screening_call: 'Screening Call',
  first_interview: 'First Interview',
  second_interview: 'Second Interview',
  additional_interview: 'Additional Interview',
  technical_assessment: 'Technical Assessment',
  reference_check: 'Reference Check',
  offer_extended: 'Offer Extended',
  negotiation: 'Negotiation',
  hired: 'Hired',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

// All statuses for the dropdown menu
const allStatuses: ApplicationStatus[] = [
  'submitted',
  'under_review',
  'screening_call',
  'first_interview',
  'second_interview',
  'additional_interview',
  'technical_assessment',
  'reference_check',
  'offer_extended',
  'negotiation',
  'hired',
  'rejected',
  'withdrawn',
];

const Applications: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { selectedId, page, pageSize, setPage, openDrawer, closeDrawer, redirectToBucket } =
    useListPageState({ basePath: '/applications' });
  const { subscribe } = useNotifications();
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; title: string } | null>(null);
  const [composeTarget, setComposeTarget] = useState<{
    applicationId: string;
    companyName: string;
    mode: 'follow_up' | 'reply';
  } | null>(null);
  const [retryLoading, setRetryLoading] = useState<string | null>(null);
  const [kitTarget, setKitTarget] = useState<{
    applicationId: string;
    roleTitle: string;
    companyName: string;
    status: ApplicationStatus;
  } | null>(null);
  const [bannerVisible, setBannerVisible] = useState(isApplicationsBannerActive());
  const navigate = useNavigate();

  const handleBannerSetUp = () => {
    captureEvent('email_addon_install_clicked', { variant: 'banner' });
    window.open(getGmailAddonUrl(), '_blank', 'noopener');
  };

  const handleBannerDismiss = () => {
    captureEvent('reply_capture_prompt_dismissed', { variant: 'banner' });
    dismissApplicationsBanner();
    setBannerVisible(false);
  };

  const autoApplyOffer = useAutoApplyOffer('applications');

  // Past AUTO_APPLY_WINS_SLOT_AT, Auto-Apply outranks Reply Capture for the one
  // offer slot. Gated on the offer actually being showable, so an already-on or
  // already-dismissed Auto-Apply silently hands the slot back rather than
  // blanking both cards.
  const autoApplyTakesPriority =
    totalCount >= AUTO_APPLY_WINS_SLOT_AT && autoApplyOffer.visible;

  const showReplyCaptureBanner = bannerVisible && totalCount > 0 && !autoApplyTakesPriority;

  useEffect(() => {
    if (showReplyCaptureBanner) {
      captureEvent('reply_capture_prompt_shown', { variant: 'banner', total_applications: totalCount });
    }
    // `totalCount` is read for the payload but must NOT be a trigger. This is a
    // shown-once event, and `showReplyCaptureBanner` is itself derived from
    // `totalCount > 0`, so the run that fires already carries the count from the
    // same commit. Listing `totalCount` would re-fire
    // `reply_capture_prompt_shown` on every page of results the user loads while
    // the banner stays up, inflating the impression count the funnel is measured
    // against.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReplyCaptureBanner]);

  // Auto-Apply discovery for people who apply from this page. One offer at a
  // time: below the priority threshold Reply Capture holds the slot, since
  // yielding only delays this card while Reply Capture returns on its own.
  const showAutoApplyOffer =
    !showReplyCaptureBanner &&
    totalCount >= AUTO_APPLY_OFFER_MIN_APPLICATIONS &&
    autoApplyOffer.visible;

  // The application the drawer opened on, pinned in its own state (as
  // OpportunitiesTab pins its opportunity). A refresh can move the open
  // application off this page — a newer one is inserted ahead of it — and a
  // purely page-derived lookup would then go null, unmounting the drawer and a
  // composer holding the user's edits (a refused follow-up keeps them open and
  // refreshes the list). A list response replaces the pin only when it holds
  // that row; one it dropped stays pinned until the drawer is closed.
  const [openedApplication, setOpenedApplication] = useState<Application | null>(null);
  const selectedApplication = useMemo<Application | null>(() => {
    if (!selectedId) return null;
    if (openedApplication?.id === selectedId) return openedApplication;
    return applications.find(a => a.id === selectedId) ?? null;
  }, [applications, selectedId, openedApplication]);
  const drawerOpen = Boolean(selectedApplication);

  // Pin on open, release on close; keyed on the id, so updates to the open row
  // never re-pin.
  useEffect(() => {
    setOpenedApplication(selectedApplication);
  }, [selectedApplication?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // The pinned id, readable from the (stable) fetch callback below.
  const openedIdRef = useRef<string | undefined>(undefined);
  openedIdRef.current = openedApplication?.id;

  // ONE generation counter for every list request, so only the most recent
  // can commit (same rule as OpportunitiesTab). Requests overlap: the mount or
  // a page change, a socket event, the refresh after a refused follow-up. An
  // older one landing last would otherwise put back the state the newer one
  // replaced — e.g. Follow Up again where the server now offers Retry Contact.
  // A superseded request leaves the rows, the total, the spinner and the error
  // toast to the live one.
  const fetchGenRef = useRef(0);
  const refreshOffPagePin = useCallback(async (
    applicationId: string,
    pageSize: number,
    isCurrent: () => boolean
  ) => {
    try {
      const loc = await getApplicationLocation(applicationId, pageSize);
      // Archived meanwhile: no active page holds it; the drawer keeps its copy.
      if (!isCurrent() || loc.bucket !== 'active') return;
      const response = await getApplications({
        skip: (loc.page - 1) * pageSize,
        limit: pageSize,
        sort_field: 'submitted_at',
        sort_method: 'desc',
      });
      if (!isCurrent()) return;
      const row = response.applications.find(a => a.id === applicationId);
      if (row) setOpenedApplication(prev => (prev && prev.id === applicationId ? row : prev));
    } catch (error) {
      // A background read beside a list that did load: the drawer keeps its
      // last copy, and the next refresh tries again.
      console.error('Refreshing the open application failed:', error);
    }
  }, []);
  const fetchApplications = useCallback(async (currentPage: number, currentPageSize: number) => {
    const gen = ++fetchGenRef.current;
    const isCurrent = () => gen === fetchGenRef.current;
    setLoading(true);
    try {
      const response = await getApplications({
        skip: (currentPage - 1) * currentPageSize,
        limit: currentPageSize,
        sort_field: 'submitted_at',
        sort_method: 'desc',
      });
      if (!isCurrent()) return;
      setApplications(response.applications);
      // Only the newest request gets here (the generation guard), so this copy
      // of the open row is the newest one too.
      setOpenedApplication(prev =>
        (prev && response.applications.find(a => a.id === prev.id)) ?? prev
      );
      setTotalCount(response.total);
      // A pinned application this page no longer holds still needs the
      // server's word — after a refused follow-up that is what turns Follow Up
      // into Retry Contact. There is no single-application read, so locate its
      // page and read that, updating only the pin: the rows on screen and the
      // URL stay as they are. Same generation: a newer request drops it.
      const pinnedId = openedIdRef.current;
      // Not awaited: the table's spinner is the list's, not this read's.
      if (pinnedId && !response.applications.some(a => a.id === pinnedId)) {
        refreshOffPagePin(pinnedId, currentPageSize, isCurrent);
      }
    } catch (error) {
      if (!isCurrent()) return;
      message.error(getApiErrorMessage(error));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [refreshOffPagePin]);

  useEffect(() => {
    fetchApplications(page, pageSize);
  }, [fetchApplications, page, pageSize]);

  // Refetch the page ON SCREEN, whenever it is called. Its callers are
  // captured long before they fire — a composer's send answers after it was
  // closed, a socket handler — and a refresh of the page they were built on
  // would take the newest generation and replace the rows now shown. The open
  // drawer's application then drops out of the list, unmounting the drawer
  // and any draft in it.
  const pageRef = useRef({ page, pageSize });
  pageRef.current = { page, pageSize };
  const refreshCurrentPage = useCallback(() => {
    fetchApplications(pageRef.current.page, pageRef.current.pageSize);
  }, [fetchApplications]);

  // A page past the end — applications archived elsewhere, so a refresh of the
  // last page comes back empty while others remain — moves to the last page
  // that has rows. Only the table's page: the URL keeps the open id, and the
  // pinned drawer and its editor are left alone.
  useEffect(() => {
    if (loading || applications.length > 0 || totalCount === 0) return;
    const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
    if (page > lastPage) setPage(lastPage, undefined, { replace: true });
    // `setPage` is rebuilt on every URL change (see the locator effect below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, applications, totalCount, page, pageSize]);

  // If a deep-linked id isn't in the current list, locate it (and possibly redirect to /archive).
  useEffect(() => {
    if (!selectedId) return;
    if (loading) return;
    // The drawer is already open on this id from its pinned copy: a refresh
    // that moved the row off the page must not chase it to its new page.
    if (openedApplication?.id === selectedId) return;
    if (applications.some(a => a.id === selectedId)) return;

    let cancelled = false;
    (async () => {
      try {
        const loc = await getApplicationLocation(selectedId, pageSize);
        if (cancelled) return;
        if (loc.bucket === 'archived') {
          redirectToBucket('/archive', selectedId, loc.page);
          return;
        }
        if (loc.page !== page) {
          setPage(loc.page, undefined, { replace: true });
        }
      } catch (error) {
        if (cancelled) return;
        message.error('Application not found');
        navigate('/applications', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // The pagination handles are read but not listed, on purpose. `setPage`,
    // `redirectToBucket` and `navigate` are all rebuilt whenever the URL
    // changes — useListPageState keys them on `searchParams` — and changing the
    // URL is exactly what this effect does. Listing them, or `page`/`pageSize`,
    // re-enters the effect from its own `setPage` before the refetch it
    // triggers has landed, firing a second getApplicationLocation for an id
    // that is already being located. `loading`
    // and the list are the honest triggers: they are what tells us whether the
    // id is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, applications, loading, openedApplication?.id]);

  useEffect(() => {
    return subscribe(
      ['application_status_changed', 'retry_contact_ready', 'recommended_action_changed'],
      refreshCurrentPage
    );
  }, [subscribe, refreshCurrentPage]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleUpdateStatus = async (applicationId: string, newStatus: ApplicationStatus) => {
    const previousStatus = applications.find(a => a.id === applicationId)?.applicationStatus ?? null;
    try {
      await updateApplicationStatus(applicationId, { applicationStatus: newStatus });
      captureEvent('application_status_changed', {
        new_status: newStatus,
        previous_status: previousStatus,
      });
      message.success(`Status updated to "${statusDisplayNames[newStatus]}"`);
      setApplications(prev => prev.map(app =>
        app.id === applicationId ? { ...app, applicationStatus: newStatus } : app
      ));
      setOpenedApplication(prev =>
        prev && prev.id === applicationId ? { ...prev, applicationStatus: newStatus } : prev
      );
      // The manual dropdown path doesn't go through the SSE ApplicationStatusNotifier,
      // so trigger the first-rejection reassurance here too (shared cap prevents dupes).
      maybeShowRejectionEncouragement(newStatus, applicationId);
    } catch (error) {
      message.error(getApiErrorMessage(error));
    }
  };

  const handleViewDetails = (record: Application) => {
    openDrawer(record.id);
  };

  const handleOpenKit = (record: Application) => {
    setKitTarget({
      applicationId: record.id,
      roleTitle: record.opportunity.title,
      companyName: record.company.name,
      status: record.applicationStatus,
    });
  };

  const handleArchive = (record: Application) => {
    setArchiveTarget({ id: record.id, title: record.opportunity.title });
    setArchiveModalOpen(true);
  };

  const handleArchiveSuccess = () => {
    if (archiveTarget) {
      setApplications(prev => prev.filter(app => app.id !== archiveTarget.id));
      setTotalCount(prev => prev - 1);
      if (selectedId === archiveTarget.id) {
        closeDrawer();
      }
    }
    setArchiveModalOpen(false);
    setArchiveTarget(null);
  };

  const handleRetryContact = (record: Application) => {
    Modal.confirm({
      title: 'Retry with different contact?',
      content: `This will archive your current application at ${record.company.name} and create a new opportunity with a different contact.`,
      okText: 'Retry Contact',
      okButtonProps: { style: { backgroundColor: '#7c3aed', borderColor: '#7c3aed' } },
      onOk: async () => {
        setRetryLoading(record.id);
        try {
          const result = await retryContact(record.opportunityId);
          captureEvent('contact_retry_initiated', {});
          message.success(
            `New opportunity created with ${result.newContact.firstName} ${result.newContact.lastName}. Review your new email.`
          );
          setApplications(prev => prev.filter(app => app.id !== record.id));
          setTotalCount(prev => prev - 1);
          if (selectedApplication?.id === record.id) {
            closeDrawer();
          }
          navigate('/opportunities');
        } catch (error) {
          message.error(getApiErrorMessage(error));
        } finally {
          setRetryLoading(null);
        }
      },
    });
  };

  const handleActionBadgeClick = (record: Application) => {
    if (!record.recommendedAction) return;
    if (record.recommendedAction === 'retry_different_contact') {
      handleRetryContact(record);
      return;
    }
    setComposeTarget({
      applicationId: record.id,
      companyName: record.company.name,
      mode: record.recommendedAction === 'reply_needed' ? 'reply' : 'follow_up',
    });
  };

  const handleComposeSent = () => {
    setComposeTarget(null);
    refreshCurrentPage();
  };

  const getStatusMenuItems = (applicationId: string): MenuProps['items'] => {
    return allStatuses.map((status) => ({
      key: status,
      label: statusDisplayNames[status],
      onClick: () => handleUpdateStatus(applicationId, status),
    }));
  };

  const columns = [
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_: unknown, record: Application) => (
        <Space direction="vertical" size={4}>
          <Tag color={statusColors[record.applicationStatus]}>
            {statusDisplayNames[record.applicationStatus]}
          </Tag>
          {isDeclaredApplication(record) && (
            <Tag color={record.channel === 'referral' ? 'gold' : 'default'}>
              {channelTagLabel(record.channel)}
            </Tag>
          )}
          {/* Auto-apply acts without the user in the loop, so a row it sent must
              never look like one they sent themselves (design §10). */}
          {record.autoApplied && <Tag color="blue">Auto-applied</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'recommendedAction',
      width: 120,
      render: (_: unknown, record: Application) => {
        // Declared apps have no email thread — reply/follow-up/retry don't apply.
        if (isDeclaredApplication(record) || !record.recommendedAction) return null;

        const isReply = record.recommendedAction === 'reply_needed';
        const isRetry = record.recommendedAction === 'retry_different_contact';

        if (isRetry) {
          return (
            <Tooltip
              title={
                // D20: refused over an address that failed its check, not ignored.
                record.recipientRejectedAt
                  ? "Your email wasn't sent because we couldn't confirm this contact's address. Try a different contact."
                  : 'No response after follow-up — try a different contact'
              }
            >
              <button
                className={`${css.actionPill} ${css.actionPillRetry}`}
                onClick={(e) => { e.stopPropagation(); handleActionBadgeClick(record); }}
                disabled={retryLoading === record.id}
              >
                <UserSwitchOutlined />
                <span>{retryLoading === record.id ? 'Retrying…' : 'Retry Contact'}</span>
              </button>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={
            isReply
              ? `A ${record.contact ? contactLabel(record.contact.contactType) : 'contact'} message needs your response`
              : 'No response for 7+ days — consider following up'
          }>
            <button
              className={`${css.actionPill} ${isReply ? css.actionPillReply : css.actionPillFollowUp}`}
              onClick={(e) => { e.stopPropagation(); handleActionBadgeClick(record); }}
            >
              {isReply ? <SendOutlined /> : <ClockCircleOutlined />}
              <span>{isReply ? 'Reply' : 'Follow Up'}</span>
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: 'Title',
      key: 'title',
      width: 420,
      render: (_: unknown, record: Application) => (
        // TODO(a11y): make this a real <button type="button">, the way
        // OpportunitiesTab's title cell already is. Deferred rather than done
        // here because the swap is not a one-liner: this file's `.titleLink`
        // styles an <a>, so it carries none of the UA-button reset
        // (appearance/background/border/padding/font/text-align/white-space/
        // max-width) that OpportunitiesTab.module.scss needed, and changing the
        // element without that CSS would visibly alter the desktop table —
        // which CLAUDE.md pins as the frozen reference. Until then the row's
        // "View" Button calls the same handleViewDetails and is
        // keyboard-reachable; unlike OpportunitiesTab this table keeps that
        // column at every viewport, so nothing is stranded behind a pointer.
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a onClick={() => handleViewDetails(record)} className={css.titleLink}>
          {record.opportunity.title}
        </a>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 250,
      render: (_: unknown, record: Application) => record.company.name,
    },
    {
      title: 'Search',
      dataIndex: ['opportunity', 'search', 'searchRole'],
      key: 'search',
      width: 200,
    },
    {
      title: 'Location',
      dataIndex: ['opportunity', 'search', 'searchLocation'],
      key: 'location',
      width: 240,
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      key: 'submitted',
      width: 130,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 360,
      render: (_: unknown, record: Application) => (
        <Space size="small">
          {canGenerateInterviewKit(record.applicationStatus) && (
            <Tooltip title="Generate a personalized prep kit for this interview">
              <button
                className={`${css.actionPill} ${css.actionPillKit}`}
                onClick={(e) => { e.stopPropagation(); handleOpenKit(record); }}
              >
                <SolutionOutlined />
                <span>Interview Kit</span>
              </button>
            </Tooltip>
          )}
          <Dropdown menu={{ items: getStatusMenuItems(record.id) }} trigger={['click']}>
            <Button size="small" icon={<EditOutlined />}>
              Update Status <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            View
          </Button>
          <Button
            size="small"
            type="text"
            icon={<InboxOutlined />}
            onClick={() => handleArchive(record)}
          >
            Archive
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={css.applications}>
      <div className={css.header}>
        <Title level={2}>Applications</Title>
        {!loading || applications.length > 0 ? (
          <Text type="secondary">{totalCount} applications</Text>
        ) : null}
      </div>

      {/* Only this area switches between loading, empty and the table. The
          drawer and the modals below always render: an open application is
          pinned (see openedApplication) and its composer may hold unsaved
          edits, so a refresh that comes back empty must never unmount them. */}
      {loading && applications.length === 0 ? (
        <Spin size="large" />
      ) : applications.length === 0 && totalCount === 0 ? (
        <div className={css.emptyContainer}>
          <Empty
            description="No applications yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <p>Applications you submit will appear here so you can track your progress.</p>
          </Empty>
          {/* Warm-path coaching (design §15): the empty state is the moment to
              steer the FIRST application through a person, not a portal. The
              ~13× is the measured Ashby ceiling for genuinely endorsed
              referrals (docs/findings/2026-06-30-outreach-vs-direct-apply-
              statistics.md) — keep the "endorsed" qualifier to avoid
              overclaiming. Not dismissible: it self-removes with the first
              application. Amber = the app's "a human you can reach" color
              (WarmPathNudge). */}
          <div className={css.emptyNudge}>
            <div className={css.emptyNudgeHeading}>
              <BulbOutlined />
              <Text strong>Applications through people convert far better than cold applies.</Text>
            </div>
            <div className={css.emptyNudgeStat}>
              A genuinely endorsed referral can be up to ~13× more likely to land an interview.
              <br />
              Open an opportunity and start with &ldquo;Apply through LinkedIn&rdquo;.
            </div>
            <div>
              <Button type="primary" onClick={() => navigate('/opportunities')}>
                Browse opportunities
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {showReplyCaptureBanner && (
            <ReplyCaptureBanner
              onSetUp={handleBannerSetUp}
              onDismiss={handleBannerDismiss}
            />
          )}

          {showAutoApplyOffer && autoApplyOffer.settings && (
            <AutoApplyOfferCard
              surface="applications"
              settings={autoApplyOffer.settings}
              onSaved={autoApplyOffer.onSaved}
              onDismiss={autoApplyOffer.dismiss}
            />
          )}

          <Table
            dataSource={applications}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => `${total} applications`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage, newPageSize);
              },
            }}
          />
        </>
      )}

      <ApplicationDrawer
        application={selectedApplication}
        open={drawerOpen}
        onClose={closeDrawer}
        onRefresh={refreshCurrentPage}
      />

      <ArchiveModal
        applicationId={archiveTarget?.id ?? null}
        applicationTitle={archiveTarget?.title ?? ''}
        open={archiveModalOpen}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveTarget(null);
        }}
        onSuccess={handleArchiveSuccess}
      />

      {composeTarget && (
        <ComposeEmailModal
          applicationId={composeTarget.applicationId}
          companyName={composeTarget.companyName}
          mode={composeTarget.mode}
          open={true}
          onClose={() => setComposeTarget(null)}
          onSuccess={handleComposeSent}
          onRefresh={refreshCurrentPage}
        />
      )}

      {kitTarget && (
        <InterviewKitModal
          applicationId={kitTarget.applicationId}
          roleTitle={kitTarget.roleTitle}
          companyName={kitTarget.companyName}
          status={kitTarget.status}
          open={true}
          onClose={() => setKitTarget(null)}
        />
      )}
    </div>
  );
};

export default Applications;
